/**
 * Block editor of blog posts (TipTap). value/onChange speak the stored HTML contract (articleSchema.js).
 * Slash menu, floating toolbar, markdown input rules + paste, image paste/drop upload, inline AI, word count.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import { Placeholder } from '@tiptap/extensions';
import Suggestion from '@tiptap/suggestion';
import { PluginKey, TextSelection } from '@tiptap/pm/state';
import { Alert, Button, Divider, Dropdown, Input, Modal, Space, Spin, Tooltip, Typography, message } from 'antd';
import { BoldOutlined, CodeOutlined, ItalicOutlined, LinkOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { t as tt, useTranslation } from '../../../../shared/i18n';
import { MediaPickerModal } from '../Media';
import { uploadImages } from './upload';
import { reportAiError, runAi } from '../ai';
import { countWords, embedProvider, looksLikeMarkdown, markdownToHtml, sanitizeArticle, tagsIn, toEditorHtml, toStoredHtml } from './articleSchema';
import { Callout, CodeBlockLang, Embed, FigureImage, Footnote, Gallery, HeadingShortcut, MarkHighlight, MarkSub, MarkSup } from './nodes';

const { Text } = Typography;

const CSS = `
.yc-be{border:1px solid var(--ant-color-border,#d9d9d9);border-radius:8px;background:var(--ant-color-bg-container,#fff)}
.yc-be .tiptap{min-height:var(--yc-be-h,420px);padding:16px 20px;outline:none;line-height:1.7;overflow-wrap:anywhere;font-size:15px;counter-reset:ycfn}
.yc-be .tiptap>*:first-child{margin-top:0}
.yc-be .tiptap p{margin:0 0 .8em}
.yc-be .tiptap h2{font-size:1.5em;margin:1.2em 0 .5em;font-weight:600}.yc-be .tiptap h3{font-size:1.25em;margin:1.1em 0 .4em;font-weight:600}.yc-be .tiptap h4{font-size:1.1em;margin:1em 0 .4em;font-weight:600}.yc-be .tiptap h5{font-size:1em;margin:1em 0 .4em;font-weight:600}
.yc-be .tiptap ul,.yc-be .tiptap ol{padding-left:1.5em;margin:0 0 .8em}.yc-be .tiptap li p{margin:0 0 .2em}
.yc-be .tiptap blockquote{border-left:3px solid var(--ant-color-border,#d9d9d9);margin:0 0 .8em;padding-left:1em;opacity:.85}
.yc-be .tiptap img{max-width:100%;height:auto;display:block;border-radius:6px}
.yc-be .tiptap :not(pre)>code{background:var(--ant-color-fill-tertiary,#f0f0f0);border-radius:4px;padding:.1em .35em;font-size:.9em}
.yc-be .tiptap hr{border:0;border-top:1px solid var(--ant-color-border,#d9d9d9);margin:1.5em 0}
.yc-be .tiptap hr.ProseMirror-selectednode{border-top-color:#2357ff}
.yc-be .tiptap aside{background:var(--ant-color-info-bg,#e6f4ff);border-radius:8px;padding:12px 16px;margin:0 0 .8em}.yc-be .tiptap aside>*:last-child{margin-bottom:0}
.yc-be .tiptap table{border-collapse:collapse;width:100%;margin:0 0 .8em;table-layout:fixed}
.yc-be .tiptap td,.yc-be .tiptap th{border:1px solid var(--ant-color-border,#d9d9d9);padding:6px 8px;vertical-align:top;position:relative}.yc-be .tiptap th{background:var(--ant-color-fill-quaternary,#fafafa);font-weight:600;text-align:left}
.yc-be .tiptap td p,.yc-be .tiptap th p{margin:0}.yc-be .tiptap .selectedCell{background:var(--ant-color-primary-bg,#e6f4ff)}
.yc-be .tiptap p.is-editor-empty:first-child::before,.yc-be .tiptap p.is-empty::before{content:attr(data-placeholder);color:var(--ant-color-text-tertiary,#999);float:left;height:0;pointer-events:none}
.yc-be .yc-be-fn{counter-increment:ycfn;color:#2357ff;cursor:pointer;font-weight:600}.yc-be .yc-be-fn::after{content:"[" counter(ycfn) "]"}
.yc-be-fig{margin:0 0 1em}.yc-be-fig.is-sel .yc-be-fig-box{outline:2px solid #2357ff;border-radius:6px}
.yc-be-fig-box{position:relative;display:block}.yc-be-x{position:absolute!important;top:6px;right:6px;opacity:0;transition:opacity .1s}.yc-be-fig-box:hover .yc-be-x,.yc-be-x:focus{opacity:1}
.yc-be-cap,.yc-be-alt{display:block;width:100%;border:0;background:transparent;outline:none;text-align:center;font-size:13px;color:inherit;padding:4px 0}.yc-be-alt{font-size:12px;color:var(--ant-color-text-tertiary,#999);padding:0}
.yc-be-gal{display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:8px}.yc-be-gal img{width:100%;height:120px;object-fit:cover}
.yc-be-embed{display:flex;align-items:center;gap:8px;border:1px solid var(--ant-color-border,#d9d9d9);border-radius:8px;padding:14px;background:var(--ant-color-fill-quaternary,#fafafa)}.yc-be-embed a{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.yc-be-code{position:relative;margin:0 0 1em}.yc-be-code select{position:absolute;top:6px;right:6px;font-size:12px;background:#2b2f36;color:#ddd;border:1px solid #555;border-radius:4px;z-index:1}
.yc-be-code pre{background:#1e2127;color:#e6e6e6;border-radius:8px;padding:34px 14px 14px;margin:0;overflow:auto;font:13px/1.55 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;min-height:64px}
.yc-be-foot{display:flex;justify-content:flex-end;gap:12px;padding:6px 12px;border-top:1px solid var(--ant-color-border-secondary,#f0f0f0);font-size:12px}
.yc-be-bubble{display:flex;align-items:center;gap:2px;padding:4px;background:var(--ant-color-bg-elevated,#fff);border:1px solid var(--ant-color-border-secondary,#f0f0f0);border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,.14)}
.yc-be-slash{position:fixed;z-index:1200;width:260px;max-height:320px;overflow:auto;background:var(--ant-color-bg-elevated,#fff);border:1px solid var(--ant-color-border-secondary,#f0f0f0);border-radius:8px;box-shadow:0 6px 24px rgba(0,0,0,.18);padding:4px}
.yc-be-slash button{display:block;width:100%;border:0;background:transparent;color:inherit;text-align:left;padding:7px 10px;border-radius:6px;cursor:pointer;font-size:14px}.yc-be-slash button.on,.yc-be-slash button:hover{background:var(--ant-color-primary-bg,#e6f4ff)}
`;

/** The slash commands. run(editor, ctx) acts after the "/query" text is removed. */
const COMMANDS = [
    { key: 'h2', label: 'Heading 2', words: 'title h2', run: (e) => e.chain().focus().setHeading({ level: 2 }).run() },
    { key: 'h3', label: 'Heading 3', words: 'title h3', run: (e) => e.chain().focus().setHeading({ level: 3 }).run() },
    { key: 'h4', label: 'Heading 4', words: 'title h4', run: (e) => e.chain().focus().setHeading({ level: 4 }).run() },
    { key: 'ul', label: 'Bulleted list', words: 'list ul', run: (e) => e.chain().focus().toggleBulletList().run() },
    { key: 'ol', label: 'Numbered list', words: 'list ol', run: (e) => e.chain().focus().toggleOrderedList().run() },
    { key: 'quote', label: 'Quote', words: 'blockquote', run: (e) => e.chain().focus().setBlockquote().run() },
    { key: 'code', label: 'Code block', words: 'pre', run: (e) => e.chain().focus().setCodeBlock().run() },
    { key: 'table', label: 'Table', words: 'grid', run: (e) => e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
    { key: 'image', label: 'Image', tctx: 'shop', words: 'photo picture upload', run: (e, c) => c.pickImage() },
    { key: 'gallery', label: 'Gallery', words: 'images photos', run: (e) => e.chain().focus().insertContent({ type: 'gallery', attrs: { images: [] } }).run() },
    { key: 'embed', label: 'Embed', words: 'video youtube vimeo spotify bilibili map x twitter', run: (e, c) => c.askEmbed() },
    { key: 'callout', label: 'Callout', words: 'note tip', run: (e) => e.chain().focus().insertContent({ type: 'callout', content: [{ type: 'paragraph' }] }).run() },
    { key: 'footnote', label: 'Footnote', words: 'reference', run: (e, c) => c.askFootnote() },
    { key: 'hr', label: 'Divider', words: 'line hr', run: (e) => e.chain().focus().setHorizontalRule().run() },
];
const labelOf = (c) => tt(c.label, c.tctx ? { context: c.tctx } : undefined);

const slashExtension = (ctxRef) => Extension.create({
    name: 'slashMenu',
    addProseMirrorPlugins() {
        return [Suggestion({
            editor: this.editor,
            char: '/',
            startOfLine: false,
            allowSpaces: false,
            pluginKey: new PluginKey('ycSlash'),
            allow: ({ editor, state, range }) => !editor.isActive('codeBlock') && state.doc.resolve(range.from).parent.type.name !== 'codeBlock',
            items: ({ query }) => {
                const q = query.toLowerCase();
                return COMMANDS.filter((c) => !q || labelOf(c).toLowerCase().includes(q) || c.label.toLowerCase().includes(q) || c.words.includes(q));
            },
            command: ({ editor, range, props }) => {
                editor.chain().focus().deleteRange(range).run();
                props.run(editor, ctxRef.current);
            },
            render: () => ({
                onStart: (p) => ctxRef.current.setSlash({ items: p.items, rect: p.clientRect?.(), command: p.command, index: 0 }),
                onUpdate: (p) => ctxRef.current.setSlash({ items: p.items, rect: p.clientRect?.(), command: p.command, index: 0 }),
                onKeyDown: ({ event }) => ctxRef.current.slashKey(event),
                onExit: () => ctxRef.current.setSlash(null),
            }),
        })];
    },
});

const SlashMenu = ({ slash, setIndex }) => {
    const ref = useRef(null);
    useEffect(() => { ref.current?.querySelector('.on')?.scrollIntoView({ block: 'nearest' }); }, [slash?.index]);
    if (!slash || !slash.items.length || !slash.rect) return null;
    const top = Math.min(slash.rect.bottom + 6, window.innerHeight - 330);
    const left = Math.min(slash.rect.left, window.innerWidth - 270);
    return createPortal(
        <div ref={ref} className="yc-be-slash" style={{ top, left }} role="listbox">
            {slash.items.map((c, i) => (
                <button key={c.key} type="button" role="option" aria-selected={i === slash.index} className={i === slash.index ? 'on' : ''}
                    onMouseEnter={() => setIndex(i)} onMouseDown={(e) => { e.preventDefault(); slash.command(c); }}>{labelOf(c)}</button>
            ))}
        </div>, document.body,
    );
};

const toneItems = ['friendly', 'professional', 'casual', 'confident'];
const TONE_LABEL = { friendly: 'Friendly', professional: 'Professional', casual: 'Casual', confident: 'Confident' };

const BUBBLE_OPTIONS = { placement: 'top' };
const textSelected = ({ editor: ed, state }) => !state.selection.empty && !ed.isActive('codeBlock') && !state.selection.node
    && state.doc.textBetween(state.selection.from, state.selection.to).trim() !== '';
const inTable = ({ editor: ed, state }) => ed.isActive('table') && state.selection.empty;

const BlockEditor = ({ value, onChange, siteId, title, docKey, minHeight = 420, placeholder }) => {
    const { t } = useTranslation();
    const [slash, setSlash] = useState(null);
    const slashRef = useRef(null);
    slashRef.current = slash;
    const [picker, setPicker] = useState(null); // callback(media)
    const [dialog, setDialog] = useState(null); // {kind: link|embed|footnote, value, pos}
    const [dropped, setDropped] = useState([]);
    const [busy, setBusy] = useState(null);
    const [ai, setAi] = useState(null); // {busy, from, to, text, result}
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const titleRef = useRef(title);
    titleRef.current = title;
    const editorRef = useRef(null);
    const fileRef = useRef(null);
    const fileCb = useRef(null);

    const noteDropped = useCallback((list) => { if (list.length) setDropped((d) => [...new Set([...d, ...list])]); }, []);

    const insertImages = useCallback(async (files, pos) => {
        const list = [...files].filter((f) => f.type.startsWith('image/'));
        if (!list.length) return;
        setBusy(t('Uploading…'));
        try {
            const rows = await uploadImages(siteId, list);
            const nodes = rows.filter((m) => m.kind === 'image').map((m) => ({ type: 'figureImage', attrs: { src: m.url, alt: m.alt || '', width: m.width, height: m.height } }));
            const ed = editorRef.current;
            if (ed && nodes.length) {
                // no focus(): the author may have moved on while it uploaded
                if (pos != null) ed.commands.insertContentAt(pos, nodes);
                else ed.commands.insertContent(nodes);
                // an inserted atom stays selected and the next key would replace it: put the cursor after it
                const { state, view } = ed;
                if (state.selection.node) view.dispatch(state.tr.setSelection(TextSelection.near(state.doc.resolve(state.selection.to), 1)));
            }
        } catch (_) { /* uploadImages reports (quota → Upgrade) */ } finally { setBusy(null); }
    }, [siteId, t]);

    const ctx = useRef({});
    ctx.current = {
        setSlash,
        slashKey: (event) => {
            const s = slashRef.current;
            if (!s || !s.items.length) return false;
            if (event.key === 'ArrowDown') { setSlash({ ...s, index: (s.index + 1) % s.items.length }); return true; }
            if (event.key === 'ArrowUp') { setSlash({ ...s, index: (s.index - 1 + s.items.length) % s.items.length }); return true; }
            if (event.key === 'Enter' || event.key === 'Tab') { s.command(s.items[s.index]); return true; }
            return false;
        },
        pick: (cb) => setPicker(() => cb),
        pickImage: () => setPicker(() => (m) => {
            editorRef.current?.chain().focus().insertContent({ type: 'figureImage', attrs: { src: m.url, alt: m.alt || '', width: m.width, height: m.height } }).run();
        }),
        chooseFiles: (cb) => { fileCb.current = cb; fileRef.current?.click(); },
        askEmbed: () => setDialog({ kind: 'embed', value: '' }),
        askFootnote: () => setDialog({ kind: 'footnote', value: '' }),
    };

    const extensions = useMemo(() => [
        StarterKit.configure({
            heading: { levels: [2, 3, 4, 5] },
            codeBlock: false,
            link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: null, target: null, class: null } },
        }),
        HeadingShortcut,
        CodeBlockLang,
        Table.configure({ resizable: false }), TableRow, TableHeader, TableCell,
        FigureImage, Gallery.configure({ ctx }), Embed, Callout, Footnote, MarkHighlight, MarkSub, MarkSup,
        Placeholder.configure({ placeholder: placeholder || tt('Write, or type / for blocks') }),
        slashExtension(ctx),
    ], []); // eslint-disable-line react-hooks/exhaustive-deps

    const load = useCallback((ed, stored) => {
        const { html, dropped: gone } = toEditorHtml(stored);
        ed.commands.setContent(html, { emitUpdate: false });
        // what the site keeps but this editor cannot represent is lost too: say so
        const shown = tagsIn(ed.getHTML());
        const lost = [...tagsIn(html)].filter((x) => !shown.has(x) && !['b', 'i', 'strong', 'em', 'span', 'tbody', 'thead', 'br', 'img', 'figcaption', 'sup'].includes(x)).map((x) => `<${x}>`);
        setDropped([...new Set([...gone, ...lost])]);
    }, []);

    const insertRef = useRef(insertImages);
    insertRef.current = insertImages;
    const editorPropsRef = useRef({
            transformPastedHTML: (html) => {
                const r = sanitizeArticle(html, { paste: true });
                noteDropped(r.dropped);
                return r.html;
            },
            handlePaste: (view, event) => {
                const dt = event.clipboardData;
                const files = [...(dt?.files || [])].filter((f) => f.type.startsWith('image/'));
                if (files.length) { event.preventDefault(); insertRef.current(files); return true; }
                const text = dt?.getData('text/plain');
                const inCode = view.state.selection.$from.parent.type.name === 'codeBlock';
                if (!inCode && text && !dt.getData('text/html') && looksLikeMarkdown(text)) {
                    editorRef.current?.chain().focus().insertContent(markdownToHtml(text)).run();
                    return true;
                }
                return false;
            },
            handleDrop: (view, event, _slice, moved) => {
                const files = [...(event.dataTransfer?.files || [])].filter((f) => f.type.startsWith('image/'));
                if (moved || !files.length) return false;
                event.preventDefault();
                insertRef.current(files, view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos);
                return true;
            },
            handleClickOn: (view, pos, node) => {
                if (node.type.name !== 'footnote') return false;
                setDialog({ kind: 'footnote', value: node.attrs.text, pos });
                return true;
            },
    });

    const editor = useEditor({
        extensions,
        shouldRerenderOnTransaction: false,
        content: '',
        editorProps: editorPropsRef.current,
        onUpdate: ({ editor: ed }) => {
            onChangeRef.current?.(toStoredHtml(ed.getHTML()));
        },
    });
    editorRef.current = editor;

    // Content is (re)loaded only when docKey changes (post opened, revision restored, draft discarded). Comparing value with
    // what was last emitted would race with fast edits and throw them away.
    const valueRef = useRef(value);
    valueRef.current = value;
    useEffect(() => {
        if (editor) load(editor, valueRef.current || '');
    }, [editor, docKey, load]);

    const live = useEditorState({
        editor,
        selector: ({ editor: ed }) => (ed ? {
            bold: ed.isActive('bold'), italic: ed.isActive('italic'), link: ed.isActive('link'), code: ed.isActive('code'),
            words: countWords(ed.state.doc.textBetween(0, ed.state.doc.content.size, ' ', ' ')),
        } : { words: 0 }),
    }) || { words: 0 };
    const words = live.words;

    const finishDialog = () => {
        const d = dialog;
        const v = String(d.value || '').trim();
        const chain = editor.chain().focus();
        if (d.kind === 'link') {
            if (!v) chain.extendMarkRange('link').unsetLink().run();
            else if (/^(https?:\/\/|mailto:|tel:|\/|#)/i.test(v)) chain.extendMarkRange('link').setLink({ href: v }).run();
            else { message.error(t('Enter a valid link')); return; }
        } else if (d.kind === 'embed') {
            if (!embedProvider(v)) { message.error(t('Paste a YouTube, Vimeo, Spotify, Bilibili, Google Maps or X link')); return; }
            chain.insertContent({ type: 'embed', attrs: { url: v } }).run();
        } else if (d.pos != null) {
            const node = editor.state.doc.nodeAt(d.pos);
            if (node?.type.name === 'footnote') {
                editor.view.dispatch(v ? editor.state.tr.setNodeMarkup(d.pos, null, { text: v }) : editor.state.tr.delete(d.pos, d.pos + 1));
            }
        } else if (v) chain.insertContent({ type: 'footnote', attrs: { text: v } }).run();
        setDialog(null);
    };

    const runInline = async (action, tone) => {
        const { from, to } = editor.state.selection;
        const text = editor.state.doc.textBetween(from, to, '\n\n', ' ');
        setAi({ busy: true, from, to, text });
        try {
            const r = await runAi(siteId, 'post_assist', { action, tone, text, title: titleRef.current || '' });
            setAi({ busy: false, from, to, text, result: r?.text || '' });
        } catch (e) { setAi(null); reportAiError(e); }
    };
    const applyAi = () => {
        const paras = String(ai.result).split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
        const $from = editor.state.doc.resolve(ai.from);
        const sameBlock = $from.sameParent(editor.state.doc.resolve(ai.to));
        const content = sameBlock && paras.length === 1 ? paras[0]
            : paras.map((p) => ({ type: 'paragraph', content: [{ type: 'text', text: p }] }));
        editor.chain().focus().insertContentAt({ from: ai.from, to: ai.to }, content).run();
        setAi(null);
    };

    const aiMenu = {
        items: [
            { key: 'rewrite', label: t('Rewrite') }, { key: 'shorten', label: t('Shorten') }, { key: 'expand', label: t('Expand') },
            { key: 'tone', label: t('Change tone'), children: toneItems.map((x) => ({ key: `tone:${x}`, label: t(TONE_LABEL[x]) })) },
        ],
        onClick: ({ key }) => (key.startsWith('tone:') ? runInline('tone', key.slice(5)) : runInline(key)),
    };
    const B = ({ icon, active, title: tip, onClick }) => (
        <Tooltip title={tip} mouseEnterDelay={0.4}>
            <Button size="small" type={active ? 'primary' : 'text'} icon={icon} aria-label={tip} onMouseDown={(e) => e.preventDefault()} onClick={onClick} />
        </Tooltip>
    );

    return (
        <div className="yc-be" style={{ '--yc-be-h': `${minHeight}px` }}>
            <style>{CSS}</style>
            {dropped.length > 0 && (
                <Alert type="warning" showIcon banner closable onClose={() => setDropped([])}
                    message={t('The site does not support this content, so it will be removed: {{list}}', { list: dropped.join(', ') })} />
            )}
            {editor && (
                <BubbleMenu editor={editor} pluginKey="ycBubble" shouldShow={textSelected} options={BUBBLE_OPTIONS}>
                    <div className="yc-be-bubble">
                        <B icon={<BoldOutlined />} title={t('Bold')} active={live.bold} onClick={() => editor.chain().focus().toggleBold().run()} />
                        <B icon={<ItalicOutlined />} title={t('Italic')} active={live.italic} onClick={() => editor.chain().focus().toggleItalic().run()} />
                        <B icon={<LinkOutlined />} title={t('Link')} active={live.link} onClick={() => setDialog({ kind: 'link', value: editor.getAttributes('link').href || '' })} />
                        <B icon={<CodeOutlined />} title={t('Code')} active={live.code} onClick={() => editor.chain().focus().toggleCode().run()} />
                        <Divider type="vertical" style={{ margin: '0 4px' }} />
                        <Dropdown menu={aiMenu} trigger={['click']} getPopupContainer={() => document.body} overlayStyle={{ zIndex: 1300 }}>
                            <Button size="small" type="text" icon={<ThunderboltOutlined />} onMouseDown={(e) => e.preventDefault()}>{t('AI')}</Button>
                        </Dropdown>
                    </div>
                </BubbleMenu>
            )}
            {editor && (
                <BubbleMenu editor={editor} pluginKey="ycTable" shouldShow={inTable} options={BUBBLE_OPTIONS}>
                    <Space size={2} className="yc-be-bubble">
                        <Button size="small" type="text" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().addRowAfter().run()}>{t('Add row')}</Button>
                        <Button size="small" type="text" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().addColumnAfter().run()}>{t('Add column')}</Button>
                        <Button size="small" type="text" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().deleteRow().run()}>{t('Delete row')}</Button>
                        <Button size="small" type="text" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().deleteColumn().run()}>{t('Delete column')}</Button>
                        <Button size="small" type="text" danger onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().deleteTable().run()}>{t('Delete table')}</Button>
                    </Space>
                </BubbleMenu>
            )}
            <EditorContent editor={editor} />
            <div className="yc-be-foot">
                {busy && <Text type="secondary"><Spin size="small" /> {busy}</Text>}
                <Text type="secondary">{t('{{n}} words', { n: words })} · {t('{{n}} min read', { n: Math.max(1, Math.round(words / 220)) })}</Text>
            </div>
            <SlashMenu slash={slash} setIndex={(i) => setSlash((s) => ({ ...s, index: i }))} />
            <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={async (e) => {
                const files = [...e.target.files];
                e.target.value = '';
                if (!files.length) return;
                setBusy(t('Uploading…'));
                try { fileCb.current?.((await uploadImages(siteId, files)).filter((m) => m.kind === 'image')); } catch (_) { /* reported */ } finally { setBusy(null); }
            }} />
            <MediaPickerModal open={!!picker} siteId={siteId} kind="image" onClose={() => setPicker(null)} onPick={(m) => picker?.(m)} />
            <Modal open={!!dialog} destroyOnHidden width={460} onCancel={() => setDialog(null)} onOk={finishDialog}
                title={{ link: t('Link'), embed: t('Embed'), footnote: t('Footnote') }[dialog?.kind]}>
                {dialog?.kind === 'footnote' ? (
                    <Input.TextArea autoFocus autoSize={{ minRows: 2 }} value={dialog.value} onChange={(e) => setDialog({ ...dialog, value: e.target.value })} />
                ) : (
                    <Input autoFocus value={dialog?.value} placeholder="https://" onChange={(e) => setDialog({ ...dialog, value: e.target.value })} onPressEnter={finishDialog} />
                )}
            </Modal>
            <Modal open={!!ai} title={t('AI suggestion')} width={620} onCancel={() => setAi(null)} destroyOnHidden
                okText={t('Replace')} okButtonProps={{ disabled: !ai?.result }} onOk={applyAi}>
                {ai?.busy ? <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div> : <div style={{ whiteSpace: 'pre-wrap', maxHeight: 420, overflow: 'auto' }}>{ai?.result}</div>}
            </Modal>
        </div>
    );
};

export default BlockEditor;
